import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SegmentPicker, ConfigSuggestion } from "@/components/SegmentSetup";
import { WhatsAppConnect } from "@/components/WhatsAppConnect";
import { Building2, Smartphone, ArrowRight, Loader2, CheckCircle2, Zap, LayoutGrid, Sparkles } from "lucide-react";

export const PERSONALITIES = [
  { v: "humanizada", l: "Humanizada", d: "Natural e empática (padrão)" },
  { v: "profissional", l: "Profissional", d: "Cordial e objetiva" },
  { v: "amigavel", l: "Amigável", d: "Calorosa e próxima" },
  { v: "objetiva", l: "Objetiva", d: "Direta, sem rodeios" },
  { v: "descontraida", l: "Descontraída", d: "Leve, com bom humor" },
];
const STEPS = [
  { icon: Zap, title: "Boas-vindas" }, { icon: Building2, title: "Negócio" }, { icon: LayoutGrid, title: "Segmento" },
  { icon: Sparkles, title: "Configuração" }, { icon: Smartphone, title: "WhatsApp" }, { icon: CheckCircle2, title: "Pronto" },
];
const LAST = STEPS.length - 1;

export function OnboardingDialog({ company, onFinished }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [c, setC] = useState({
    name: company.name || "", ai_tone: company.ai_tone || "humanizada", ai_personality: company.ai_personality || "",
    ai_name: company.ai_name || "", owner_whatsapp: company.owner_whatsapp || "", whatsapp_status: company.whatsapp_status || "desconectado",
    segment: company.segment || "",
  });
  const set = (k) => (e) => setC((p) => ({ ...p, [k]: e.target.value }));

  const saveName = async () => {
    if (!c.name.trim()) { toast.error("Informe o nome do negócio."); return; }
    setSaving(true);
    try { await api.put("/company", { name: c.name }); setStep(2); } catch { toast.error("Erro ao salvar."); } finally { setSaving(false); }
  };
  const acceptConfig = async (cfg) => {
    setSaving(true);
    try { await api.post("/company/apply-config", cfg); setC((p) => ({ ...p, segment: cfg.segment })); toast.success("Agenda e serviços configurados!"); setStep(c.whatsapp_status === "conectado" ? 5 : 4); }
    catch { toast.error("Erro ao aplicar configuração."); } finally { setSaving(false); }
  };
  const saveWa = async () => {
    setSaving(true);
    try { await api.put("/company", { owner_whatsapp: c.owner_whatsapp }); setStep(5); }
    catch { toast.error("Erro ao salvar."); } finally { setSaving(false); }
  };
  const finish = async () => {
    setSaving(true);
    try { await api.put("/company", { onboarded: true }); toast.success("Tudo pronto! Sua agenda já está funcionando."); onFinished(); }
    catch { toast.error("Erro ao salvar."); } finally { setSaving(false); }
  };
  const skipStep = () => setStep(step === 3 ? (c.whatsapp_status === "conectado" ? 5 : 4) : step + 1);


  return (
    <Dialog open>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-950 text-slate-100 [&>button]:hidden" data-testid="onboarding-dialog">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2"><div className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-400 text-slate-950"><Zap className="h-4 w-4" strokeWidth={2.6} /></div><span className="font-display font-extrabold text-white">Flow<span className="text-cyan-400">IA</span></span></div>
          <DialogTitle className="font-display text-2xl">{["Olá! Vamos configurar seu negócio em poucos minutos.", "Qual é o nome do seu negócio?", "Qual é o seu segmento?", "Revise a configuração sugerida", "Conecte o WhatsApp", "Tudo pronto! Sua agenda já está funcionando."][step]}</DialogTitle>
          <DialogDescription className="text-slate-400" data-testid="onboarding-step-counter">Passo {step + 1} de {STEPS.length} · tudo continua editável em Configurações.</DialogDescription>
        </DialogHeader>

        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-cyan-400 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
        <div className="flex gap-1.5" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s.title} className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-[11px] font-medium transition-all ${i === step ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300" : i < step ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-slate-800 text-slate-500"}`}>
              {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />} <span className="hidden sm:inline">{s.title}</span>
            </div>
          ))}
        </div>

        <div className="min-h-[220px] space-y-4 py-2">
          {step === 0 && (
            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5 text-sm text-slate-300" data-testid="onboarding-welcome">
              <p>Em 4 passos rápidos: nome do negócio → segmento → revisão da configuração que a IA sugere → WhatsApp. Depois disso sua assistente já atende e agenda sozinha.</p>
              <p className="mt-2 text-xs text-slate-500">Você pode pular etapas e voltar depois em "Como usar".</p>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-1.5"><Label className="text-slate-300">Nome do negócio</Label><Input data-testid="onboarding-company-name" value={c.name} onChange={set("name")} placeholder="Ex.: Clínica Bella, Barbearia do Zé" className="border-slate-700 bg-slate-900/60" /></div>
          )}
          {step === 2 && <SegmentPicker companyName={c.name} onSuggest={(cfg) => { setSuggestion(cfg); setStep(3); }} />}
          {step === 3 && suggestion && <ConfigSuggestion config={suggestion} onAccept={acceptConfig} onBack={() => setStep(2)} />}
          {step === 4 && (
            <>
              <div className="space-y-1.5"><Label className="text-slate-300">Seu WhatsApp pessoal (para receber relatórios e alertas)</Label><Input data-testid="onboarding-owner-phone" value={c.owner_whatsapp} onChange={set("owner_whatsapp")} placeholder="+55 19 99404-5522" className="border-slate-700 bg-slate-900/60" /></div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3"><p className="mb-2 text-sm font-medium text-slate-100">WhatsApp da empresa</p><WhatsAppConnect compact showDisconnect={false} onStatus={(d) => setC((p) => ({ ...p, whatsapp_status: d.status }))} /></div>
            </>
          )}
          {step === 5 && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm text-slate-200" data-testid="onboarding-final">
              <p className="flex items-center gap-2 font-semibold text-emerald-300"><CheckCircle2 className="h-5 w-5" /> {c.segment ? `Segmento ${c.segment} configurado.` : "Configuração salva."} {c.whatsapp_status === "conectado" ? "WhatsApp conectado — a IA já está atendendo." : "Conecte o WhatsApp em Configurações quando quiser que a IA comece a atender."}</p>
              <p className="mt-2 text-xs text-slate-400">No Dashboard você encontra o tutorial completo em 10 etapas e, no menu, a Central de Ajuda "Como usar".</p>
            </div>
          )}
        </div>

        {step !== 2 && (
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(step === 4 ? 3 : step - 1)} className={`text-sm text-slate-400 hover:text-slate-200 ${step === 0 || step === 5 ? "invisible" : ""}`} data-testid="onboarding-back">Voltar</button>
            <div className="flex items-center gap-3">
              {(step === 3 || step === 4) && <button onClick={skipStep} className="text-sm text-slate-400 hover:text-slate-200" data-testid="onboarding-skip">Pular por agora</button>}
              {step !== 3 && (
                <Button onClick={step === 0 ? () => setStep(1) : step === 1 ? saveName : step === 4 ? saveWa : finish} disabled={saving} data-testid="onboarding-next" className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : step === 0 ? "Começar" : step === LAST ? "Ir para o Dashboard" : <>Continuar <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
